import { ContextProvider } from '@lit/context';
import { AxiosError, isAxiosError } from 'axios';
import { LitElement } from 'lit';

import { ErrorsDto, postUploadLogo, postUploadPrintExample } from '@/api-sdk';
import { uploadService } from '@/context';
import { ApiService, ErrorType, UploadExtensionCheck } from '@/types';

const MAX_FILE_SIZE = 104857600;
const INVALID_FILE_EXTENSIONS = /\.(app|exe)$/i;
const PDF_FILE_EXTENSION = /\.pdf$/i;

/**
 * Uploads a file to a specified API endpoint and handles the response and errors.
 *
 * @param {LitElement} context - The context in which the upload is performed.
 * @param {Object} formData - The form data containing the file to be uploaded.
 * @param {ApiService} postApiService - The API service to which the file is uploaded ('logo' or 'printExample').
 * @param {string} customSuccessEvent - The name of the custom event to dispatch on successful upload.
 * @param {string} customErrorEvent - The name of the custom event to dispatch on upload error.
 * @param {string | undefined} locale - Current locale
 */
export const uploadFileToEndpoint = async (
  context: LitElement,
  formData: { file: File },
  postApiService: ApiService,
  customSuccessEvent: string,
  customErrorEvent: string,
  locale?: string,
) => {
  let isError = false;

  const uploadServiceContext = new ContextProvider(context, {
    context: uploadService,
    initialValue: {
      service:
        postApiService === 'logo'
          ? postUploadLogo({
              params: { locale },
              data: formData,
            })
          : postUploadPrintExample({ data: formData }),
      controller: new AbortController(),
    },
  });

  try {
    const response = await uploadServiceContext.value?.service;

    setTimeout(() => {
      if (response) {
        context.dispatchEvent(
          new CustomEvent(customSuccessEvent, {
            composed: true,
            detail: response,
          }),
        );
      }
    }, 1000);
  } catch (error: any) {
    if (isAxiosError(error) && error?.name === 'CanceledError') {
      return;
    }

    const errorData = error as AxiosError<ErrorsDto>;
    const errorDetail = errorData.response?.data?.errors?.[0]?.detail;

    if (errorDetail) {
      context.dispatchEvent(
        new CustomEvent(customErrorEvent, {
          detail: {
            error: errorDetail,
          },
        }),
      );
      return;
    }
    isError = true;
  } finally {
    uploadServiceContext.setValue(undefined);
  }
  if (isError) {
    context.dispatchEvent(
      new CustomEvent(customErrorEvent, {
        detail: ErrorType.FILE_UPLOAD,
      }),
    );
  }
};

export const uploadDragOverHandler = (event: DragEvent) => {
  event.preventDefault();
  return true;
};

export const uploadDragEndHandler = (event: DragEvent) => {
  event.preventDefault();
  return true;
};

/**
 * Handles the event when a file is dropped into a designated area.
 *
 * @param {DragEvent} event - The drag event.
 * @param {UploadExtensionCheck} [fileCheck] - Optional file extension check.
 * @returns {Object} - An object containing the file, file name, and any error that occurred.
 */
export const uploadDropHandler = (
  event: DragEvent,
  fileCheck?: UploadExtensionCheck,
):
  | { file: File | null; fileName: string; error: ErrorType | null }
  | undefined => {
  event.preventDefault();
  let error: ErrorType | null = null;

  if (!event?.dataTransfer?.items && event?.dataTransfer?.items.length !== 1) {
    return;
  }

  const data = event.dataTransfer.items[0];

  if (data.kind === 'file') {
    let file = data.getAsFile();

    if (
      (fileCheck === undefined &&
        file &&
        INVALID_FILE_EXTENSIONS.test(file.name)) ||
      (fileCheck === 'pdf' && file && !PDF_FILE_EXTENSION.test(file.name))
    ) {
      error = ErrorType.FILE_EXTENSION;
      file = null;
    }

    if (file && file.size > MAX_FILE_SIZE) {
      error = ErrorType.FILE_SIZE;
      file = null;
    }

    return {
      file: file,
      fileName: file?.name || '',
      error: error,
    };
  }
};

/**
 * Handles the input event when a file is selected through an input element.
 *
 * @param {Event} event - The input event triggered by the file selection.
 * @param {string} [fileCheckExtension] - Optional file extension to check against.
 * @returns {Object} - An object containing the file, file name, and any error that occurred.
 */
export const uploadInputEvent = (
  event: Event,
  fileCheckExtension?: string,
):
  | { file: File | null; fileName: string; error: ErrorType | null }
  | undefined => {
  const input = event.target as HTMLInputElement;
  let error: ErrorType | null = null;

  if (!input.files || !input.files?.length) {
    return;
  }

  let file: File | null = input.files[0];
  if (
    (file && INVALID_FILE_EXTENSIONS.test(file.name)) ||
    (fileCheckExtension &&
      file &&
      !file.name.includes(`.${fileCheckExtension}`))
  ) {
    input.value = '';
    error = ErrorType.FILE_EXTENSION;
    file = null;
  }

  if (file && file.size >= MAX_FILE_SIZE) {
    error = ErrorType.FILE_SIZE;
    file = null;
  }

  return { file: file, fileName: file?.name || '', error: error };
};
